import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { usePublish } from '@atlaskit/rovo-triggers/main';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { useMessageInstances } from '@atlassian/post-office-headless-api/hooks/useMessageInstances';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { CHAT_OPEN_SOURCE, MESSAGE_TEMPLATE_ID } from '../../common/constants';
import { type RovoReintroductionLauncherProps } from '../../types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports -- the launcher composes the modal, which is also the package's standalone public component
import { RovoReintroductionModal } from '../rovo-reintroduction-modal';

import { messages } from './messages';

/**
 * Query param used by the dev-only override below.
 */
const DEV_FORCE_OPEN_PARAM = 'forceRovoReintroductionModal';

/**
 * Dev-only escape hatch for manual testing: append
 * `?forceRovoReintroductionModal=true` to the URL to force the modal open,
 * bypassing the experiment, Post Office eligibility, and the Rovo-chat-open
 * check.
 *
 * Honored ONLY outside production builds (`process.env.NODE_ENV !== 'production'`),
 * so it can never bypass those checks for shipped users — even if the param
 * happens to be present on a production URL.
 */
function isDevForceOpen(): boolean {
	if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') {
		return false;
	}
	try {
		return new URLSearchParams(window.location.search).get(DEV_FORCE_OPEN_PARAM) === 'true';
	} catch {
		return false;
	}
}

/**
 * Public entrypoint. Mounted high in the host product's app shell. Renders
 * nothing unless Post Office returns an eligible message and the user is in the
 * experiment's test cohort.
 *
 * Eligibility, dismissal, and fatigue are owned by Post Office: the launcher
 * asks {@link useMessageData} whether there is a message to show for this
 * template, and a returned message means the user is eligible. The launcher no
 * longer resolves audience targeting client-side (previously two TAP traits and
 * a first-time-user check) and no longer tracks dismissal in `localStorage`.
 *
 * Analytics are wired through the Post Office message lifecycle. The modal is
 * rendered inside the `PostOfficeMessage` wrapper returned by
 * {@link useMessageData}, so its impression analytics (the modal's
 * `FireScreenAnalytics` screen event) fire in lockstep with Post Office's
 * "viewed" lifecycle — only once the choreographer actually allows the message
 * to render. Dismissal analytics (the modal's close / CTA click events) fire
 * alongside `dismissMessage`, which is Post Office's dismiss signal.
 *
 * The Statsig experiment is layered on top as a second gate: it is resolved
 * only once Post Office has returned a message (and no Rovo chat is open), so
 * the exposure event fires exclusively for users who could actually be shown
 * the modal. Post Office handles eligibility/dismissal/fatigue; Statsig handles
 * the experiment cohort split.
 */
export function RovoReintroductionLauncher(
	props: RovoReintroductionLauncherProps,
): JSX.Element | null {
	return <RovoReintroductionLauncherInner {...props} />;
}

function RovoReintroductionLauncherInner({
	product,
	openChatMode = 'sidebar',
	prompt,
	testId,
}: RovoReintroductionLauncherProps): JSX.Element | null {
	const devForceOpen = isDevForceOpen();
	const [isOpen, setIsOpen] = useState(devForceOpen);
	const { mode } = useRovoExperienceAPIStore();
	const publishRovo = usePublish('ai-mate');
	const { formatMessage } = useIntl();

	// Post Office owns eligibility, dismissal, and fatigue. Presence of a message
	// for this template means the user is eligible to be shown the modal.
	// `PostOfficeMessage` is the wrapper that fires Post Office's impression
	// ("viewed") lifecycle when the modal actually renders.
	const {
		messages: postOfficeMessages,
		PostOfficeMessageInstance,
		dismissMessage,
	} = useMessageInstances({
		placementId: 'headless',
		messageTemplateId: MESSAGE_TEMPLATE_ID,
	});
	const message = postOfficeMessages?.[0];
	const messageInstanceId = message?.messageInstanceId;

	// A Rovo chat is already open in this session. Used as a pre-render guard
	// below (after the Post Office eligibility check) so the reintroduction modal
	// never stacks on top of an open chat.
	const isChatOpen = mode !== undefined;

	// Open at most once per mount, after Post Office confirms an eligible message.
	// The experiment (and its exposure event) is resolved only once a message is
	// available and no chat is open, so the Statsig exposure fires exclusively for
	// users we could actually show the modal to.
	const hasOpenedRef = useRef(false);
	useEffect(() => {
		if (devForceOpen || hasOpenedRef.current || !messageInstanceId || isChatOpen) {
			return;
		}
		hasOpenedRef.current = true;
		setIsOpen(true);
	}, [devForceOpen, messageInstanceId, isChatOpen]);

	const handleOpenChat = useCallback(() => {
		publishRovo({
			type: 'chat-new',
			source: CHAT_OPEN_SOURCE,
			openChat: true,
			openChatMode,
			data: { dialogues: [], prompt: prompt ?? formatMessage(messages.chatPrompt) },
		});
		if (messageInstanceId) {
			dismissMessage(messageInstanceId);
		}
		setIsOpen(false);
	}, [dismissMessage, formatMessage, messageInstanceId, openChatMode, prompt, publishRovo]);
	// ✅ ADD — client-side only: close modal
	const handleClientClose = useCallback(() => {
		if (messageInstanceId) {
			dismissMessage(messageInstanceId);
		}
		setIsOpen(false);
	}, [dismissMessage, messageInstanceId]);
	if (!isOpen || !PostOfficeMessageInstance || !messageInstanceId) {
		return null;
	}

	// Pre-render guard (after the Post Office eligibility check): never render on
	// top of an already-open Rovo chat.
	if (isChatOpen) {
		return null;
	}

	// Render through the Post Office message wrapper so the impression lifecycle
	// (Post Office "viewed" events + the modal's own `FireScreenAnalytics`) fires
	// only when the choreographer allows the message to show.
	return (
		<PostOfficeMessageInstance messageInstanceId={messageInstanceId}>
			{() => (
				<RovoReintroductionModalWithDismiss
					analyticsAttributes={{ product }}
					onOpenChat={handleOpenChat}
					onClose={handleClientClose}
					testId={testId}
				/>
			)}
		</PostOfficeMessageInstance>
	);
}

/**
 * Inner wrapper rendered inside PostOfficeMessageInstance so that
 * useMessageInstanceEvent has access to the Post Office provider context.
 * This is required for the dismiss network request to actually fire.
 */
function RovoReintroductionModalWithDismiss({
	analyticsAttributes,
	onOpenChat,
	onClose,
	testId,
}: {
	analyticsAttributes: { product: string };
	onClose: () => void;
	onOpenChat: () => void;
	testId?: string;
}) {
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const fireDismissEvent = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss-rovo-reintroduction',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {},
		});
	}, [messageInstanceClicked]);
	const handleClickPrimary = useCallback(() => {
		fireDismissEvent();
		onOpenChat();
	}, [fireDismissEvent, onOpenChat]);
	const handleClose = useCallback(() => {
		fireDismissEvent();
		onClose();
	}, [fireDismissEvent, onClose]);
	return (
		<RovoReintroductionModal
			analyticsAttributes={analyticsAttributes}
			onClickPrimary={handleClickPrimary}
			onClose={handleClose}
			testId={testId}
		/>
	);
}
